'use client';
import { IProductCart } from '@/shared/api/getProduct/types';
import { useCityStore } from '@/shared/store/cityStore';
import { Container } from '@/shared/ui/Container';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { ApplicationMethodTab } from '@/widgets/product-tabs/ui/ApplicationMethodTab';
import { DeliveryAndPaymentTab } from '@/widgets/product-tabs/ui/DeliveryAndPaymentTab/DeliveryAndPaymentTab';
import { DescriptionTab } from '@/widgets/product-tabs/ui/DescriptionTab';
import { ReviewsAndQaTab } from '@/widgets/product-tabs/ui/ReviewsAndQaTab/ReviewsAndQaTab';
import { TabButton } from '@/widgets/product-tabs/ui/TabButton';
import { cva, cx } from 'class-variance-authority';
import { FC, useMemo, useState } from 'react';

interface Props {
  className?: string;
  data: IProductCart;
}

export const ProductTabs: FC<Props> = ({ className, data }) => {
  const city = useCityStore((state) => state.city);
  const cityName = useCityStore((state) => state.cityName);
  const cityShops = useMemo(() => {
    if (!city) return [];

    return data?.amount?.find((i) => i.city.code === city)?.store || [];
  }, [city]);
  const shopsAmount = useMemo(() => {
    if (!city) {
      return [];
    }

    const storesAvailable = cityShops.filter((i) => i.amount > 0) || [];

    if (storesAvailable.length > 0) {
      return [
        {
          name: cityName,
          coordinates: storesAvailable[0].store.map,
          totalAmount: storesAvailable.reduce((acc, i) => acc + i.amount, 0),
          shops: storesAvailable.map((i) => ({
            ...i.store,
            amount: i.amount,
          })),
        },
      ];
    }

    return [];
  }, [city, cityName]);

  const defaultShopAmount = useMemo(() => {
    const defaultCity = data?.amount?.find((i) => i.city.code === 'default');
    const defaultShop = defaultCity?.store.find((i) => i.store.uf_mainstore_delivery);
    return defaultShop?.amount;
  }, []);

  const [activeIndex, setActiveIndex] = useState(0);

  const [selectedTab, setSelectedTab] = useState(-1); // for mobile version tabs

  return (
    <>
      <div className={twMergeExt('hidden md:block', className)}>
        <Container className={cvaRoot()} variant={'large'}>
          <div className={cvaButtons()}>
            {buttons.map((i, index, arr) => (
              <TabButton
                key={index}
                style={{
                  zIndex: index === activeIndex ? '10' : arr.length - index,
                }}
                isActive={activeIndex === index}
                onClick={() => setActiveIndex(index)}
                notFirst={index !== 0}
                index={index}
                className={
                  !shopsAmount[0]?.totalAmount && !defaultShopAmount && i.title === 'Доставка и оплата' ? 'hidden' : ''
                }
              >
                {i.title}
              </TabButton>
            ))}
          </div>

          <div className={cvaContentContainer()}>
            {
              [
                <DescriptionTab data={data} key={0} />,
                // <CompoundTab
                //   text={data.component?.join(', ')}
                //   key={1}
                // />,
                <ApplicationMethodTab text={data.usage_method} key={2} />,
                <DeliveryAndPaymentTab data={data} key={3} />,
                <ReviewsAndQaTab data={data} key={4} />,
              ][activeIndex]
            }
          </div>
        </Container>
      </div>
      <div
        className={twMergeExt(
          'md:hidden flex flex-col w-[calc(100%-4.8rem)] overflow-hidden mx-2.4 my-6.4 rounded-[1.6rem] outline outline-1 -outline-offset-[1px] outline-[#DBDEE1]',
          className
        )}
      >
        {buttons.map((button, index) => {
          return (
            <div
              key={index}
              className={cx(
                'flex items-center flex-col gap-3.2 justify-center pt-1.4 pb-1.8 bg-transparent border border-b-0 rounded-bl-none rounded-br-none border-[#DBDEE1] rounded-[1.6rem]',
                !shopsAmount[0]?.totalAmount && !defaultShopAmount && button.title === 'Доставка и оплата'
                  ? 'hidden'
                  : ''
              )}
              onClick={() => setSelectedTab(index)}
            >
              {button.title}
              <div className={`${selectedTab === index ? 'max-h-fit block' : 'max-h-0 hidden'}`}>
                {selectedTab === 0 ? <DescriptionTab data={data} key={0} /> : null}
                {selectedTab === 1 ? <ApplicationMethodTab text={data.usage_method} key={2} /> : null}
                {selectedTab === 2 ? <DeliveryAndPaymentTab data={data} key={3} /> : null}
                {selectedTab === 3 ? <ReviewsAndQaTab data={data} key={4} /> : null}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

const buttons = [
  { title: 'Описание' },
  // { title: 'Состав' },
  { title: 'Способ применения' },
  { title: 'Доставка и оплата' },
  { title: 'Отзывы и вопросы', badge: 10 },
];

const cvaRoot = cva(['ProductTabs-cvaRoot', 'my-[10.4rem]']);

const cvaButtons = cva(['ProductTabs-cvaButtons', 'flex flex-col md:grid md:grid-cols-5', 'pr-1.4']);

const cvaContentContainer = cva([
  'ProductTabs-cvaContentContainer',
  'relative z-[9]',
  'p-8.8 rounded-[3.2rem] rounded-tl-none',
  'border border-[#DBDEE1]',
  'lg:min-h-[40rem]',
]);
